import { useSyncExternalStore } from "react";
import { runsOnDevice } from "../api/client.js";
import { brand } from "../brand.js";
import { getEntitlement, subscribeEntitlement, type PlaybackQuotaKind } from "./playbackUsageStore.js";
import { showUpgradePrompt } from "./upgradePrompt.js";

/**
 * What the free tier does not include (owner, 2026-10-10 and 2026-10-13; ADR "Pro, the free tier,
 * devices and promo codes"). One typed list, one check: "gating stays typed feature flags checked
 * against an entitlement, never separate code paths".
 *
 * Pro only: catch-up, recordings, favourites, the phone remote, custom remote control, timeshift
 * (pause and rewind live TV) and multiview. A free viewer also has one profile and one playlist.
 * Free: picture-in-picture, Social, parental controls and the PIN, and everything not listed.
 *
 * WHEN A LOCK APPLIES. Only in the Android app, only in SliqueTV or TV My Way (a tenant's viewers
 * pay their provider, never us), and only while the entitlement is Free. The website is not
 * locked: it has no way to become Pro. Existing data is never touched: a favourite already saved
 * stays, a second profile that exists stays; only ADDING more, or starting the feature, asks.
 *
 * What happens at a lock is the same everywhere (components/UpgradePrompt.tsx): the action does not
 * run and the viewer is told which feature is Pro and where to upgrade. Nothing is hidden, so no
 * press goes nowhere.
 */

export type ProFeature = "catchup" | "recordings" | "favorites" | "phoneRemote" | "customRemote" | "timeshift" | "multiview";

/** Limits of a free viewer that are counts, not features. */
export const FREE_LIMITS = { profiles: 1, playlists: 1 } as const;

/** Everything the upgrade prompt can be about: the time allowances, the features and the counts. */
export type UpgradeReason = PlaybackQuotaKind | ProFeature | "profiles" | "playlists";

export const PRO_FEATURES: readonly ProFeature[] = [
  "catchup",
  "recordings",
  "favorites",
  "phoneRemote",
  "customRemote",
  "timeshift",
  "multiview",
];

/** Whether this installation is held to the free tier's locks right now. */
export function freeTierApplies(): boolean {
  if (!runsOnDevice()) return false;
  if (brand().channel === "tenant") return false;
  return getEntitlement() !== "paid";
}

export function isFeatureUnlocked(_feature: ProFeature): boolean {
  return !freeTierApplies();
}

/**
 * Use at the start of an action a free viewer may not take: true means go ahead; false means the
 * viewer has been told (the upgrade prompt is up) and the action must not run. For "profiles" and
 * "playlists" the caller has already seen the count is past the free limit.
 */
export function requireFeature(reason: ProFeature | "profiles" | "playlists"): boolean {
  if (!freeTierApplies()) return true;
  showUpgradePrompt(reason);
  return false;
}

/** Reactive: a screen re-renders the moment the entitlement changes (the Free/Paid switch). */
export function useFeatureUnlocked(feature: ProFeature): boolean {
  useSyncExternalStore(subscribeEntitlement, getEntitlement, () => "paid");
  return isFeatureUnlocked(feature);
}
